import { getFirebaseInstances } from '../config/firebase/firebase';
import { doc, getDoc, setDoc, writeBatch } from 'firebase/firestore';
import logger from '../utils/logger';
import { validateOllamaUrl, isValidModelName } from '../utils/validation';

/**
 * User Settings Service
 * Centralized service for managing user settings in Firestore
 * All user preferences are stored in the users/{userId} document
 */

/**
 * Default settings structure for new users
 */
const DEFAULT_SETTINGS = {
  appearance: {
    theme: 'light',
    compactMode: false,
    fontSize: 'medium'
  },
  aiAssistant: {
    selectedModel: 'qwen2.5:14b', // Default; overridden by AI Engine hardware detection
    temperature: 0.7,
    maxTokens: 2048,
    streamingEnabled: true,
    backend: 'ollama',
    modelSelectionCompleted: false,
    modelDownloaded: false,
    inferenceMode: 'cloud',
    ollamaUrl: 'http://localhost:11434',
    ollamaModel: '' // Empty — resolved at runtime by AIEngineContext from hardware tier
  }
};

/**
 * True when stored settings still look like the pre-cloud signup seed:
 * local mode was never completed (no downloaded model and no selected Ollama model).
 * Working local setups are left alone.
 */
export const isIncompleteLocalInferenceSeed = (aiAssistant = {}) => {
  if (!aiAssistant || aiAssistant.inferenceMode !== 'ollama') {
    return false;
  }
  if (aiAssistant.modelDownloaded === true) {
    return false;
  }
  const ollamaModel = typeof aiAssistant.ollamaModel === 'string' ? aiAssistant.ollamaModel.trim() : '';
  return !ollamaModel;
};

export const SETTINGS_VALIDATION = {
  displayNameMaxLength: 120,
  nicknameMaxLength: 80,
  avatarMaxLength: 64,
  // Dark remains a stored/legacy value but is parked. Only light is active.
  themes: ['dark', 'light'],
  fontSizes: ['small', 'medium', 'large'],
  inferenceModes: ['ollama', 'cloud'],
  minTemperature: 0,
  maxTemperature: 1,
  minTokens: 512,
  maxTokens: 4096
};

const asTrimmedString = (value) => (typeof value === 'string' ? value.trim() : '');

const requireUserId = (userId) => {
  const normalized = asTrimmedString(userId);
  if (!normalized) {
    throw new Error('A valid user ID is required.');
  }
  return normalized;
};

const normalizeBoundedString = (value, maxLength, fieldName, { required = false } = {}) => {
  const normalized = asTrimmedString(value);
  if (required && !normalized) {
    throw new Error(`${fieldName} is required.`);
  }
  if (normalized.length > maxLength) {
    throw new Error(`${fieldName} must be ${maxLength} characters or fewer.`);
  }
  return normalized;
};

export const normalizeProfileSettings = (profileSettings = {}) => ({
  displayName: normalizeBoundedString(
    profileSettings.displayName,
    SETTINGS_VALIDATION.displayNameMaxLength,
    'Display name',
    { required: true }
  ),
  nickname: normalizeBoundedString(profileSettings.nickname, SETTINGS_VALIDATION.nicknameMaxLength, 'Nickname'),
  avatar: normalizeBoundedString(profileSettings.avatar, SETTINGS_VALIDATION.avatarMaxLength, 'Avatar')
});

/** Parked: Espresso + Cream is the only active theme. Dark can be restored later. */
export const resolveActiveTheme = (_theme) => 'light';

export const normalizeAppearanceSettings = (appearanceSettings = {}) => {
  const requestedTheme = appearanceSettings.theme;
  const compactMode = appearanceSettings.compactMode;
  const fontSize = appearanceSettings.fontSize;

  if (requestedTheme !== undefined && !SETTINGS_VALIDATION.themes.includes(requestedTheme)) {
    throw new Error('Theme must be dark or light.');
  }
  if (typeof compactMode !== 'boolean') {
    throw new Error('Compact mode must be enabled or disabled.');
  }
  if (!SETTINGS_VALIDATION.fontSizes.includes(fontSize)) {
    throw new Error('Font size must be small, medium, or large.');
  }

  return { theme: resolveActiveTheme(requestedTheme), compactMode, fontSize };
};

const normalizeNumberInRange = (value, min, max, fieldName) => {
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number) || number < min || number > max) {
    throw new Error(`${fieldName} must be between ${min} and ${max}.`);
  }
  return number;
};

export const normalizeAISettings = (aiSettings = {}) => {
  const selectedModel = normalizeBoundedString(
    aiSettings.selectedModel || DEFAULT_SETTINGS.aiAssistant.selectedModel,
    128,
    'Selected model',
    { required: true }
  );
  const inferenceMode = aiSettings.inferenceMode || DEFAULT_SETTINGS.aiAssistant.inferenceMode;
  const normalized = {
    selectedModel,
    temperature: normalizeNumberInRange(
      aiSettings.temperature,
      SETTINGS_VALIDATION.minTemperature,
      SETTINGS_VALIDATION.maxTemperature,
      'Temperature'
    ),
    maxTokens: normalizeNumberInRange(
      aiSettings.maxTokens,
      SETTINGS_VALIDATION.minTokens,
      SETTINGS_VALIDATION.maxTokens,
      'Max tokens'
    ),
    streamingEnabled: Boolean(aiSettings.streamingEnabled)
  };

  if (!SETTINGS_VALIDATION.inferenceModes.includes(inferenceMode)) {
    throw new Error('Inference mode must be local or cloud.');
  }
  normalized.inferenceMode = inferenceMode;

  if (aiSettings.ollamaUrl !== undefined || inferenceMode === 'ollama') {
    const validUrl = validateOllamaUrl(aiSettings.ollamaUrl || DEFAULT_SETTINGS.aiAssistant.ollamaUrl);
    if (!validUrl) {
      throw new Error('Ollama URL must be a valid http or https URL.');
    }
    normalized.ollamaUrl = validUrl;
  }

  if (aiSettings.ollamaModel !== undefined) {
    const modelName = asTrimmedString(aiSettings.ollamaModel);
    if (modelName) {
      if (!isValidModelName(modelName)) {
        throw new Error('Ollama model name contains unsupported characters.');
      }
      normalized.ollamaModel = modelName;
    } else {
      normalized.ollamaModel = '';
    }
  }

  return normalized;
};

const ensureUserDocument = async (userId) => {
  const { db, auth } = await getFirebaseInstances();
  const userDocRef = doc(db, 'users', userId);
  const userDocSnap = await getDoc(userDocRef);

  if (!userDocSnap.exists()) {
    const email = auth.currentUser?.email || '';
    await initializeDefaultSettings(userId, email, {
      displayName: auth.currentUser?.displayName || ''
    });
  }

  return { db, userDocRef };
};

/**
 * Initialize default settings for a new user
 * Called during signup to create the user document with defaults
 *
 * @param {string} userId - The user's UID
 * @param {string} email - The user's email address
 * @param {object} additionalData - Any additional data to include
 * @param {string} additionalData.displayName - User's display name
 * @param {string} additionalData.fullName - User's full name
 * @param {string} additionalData.company - Company/Organization name
 * @param {string} additionalData.jobTitle - Job title/role
 * @param {string} additionalData.userType - User type (Attorney, Investor, General)
 * @param {string} additionalData.useCase - Primary use case
 * @param {string} additionalData.referralSource - How they heard about VerdeKai
 * @param {string} additionalData.country - Country/Region
 * @param {string} additionalData.phoneNumber - Phone number (optional)
 * @param {boolean} additionalData.marketingConsent - Marketing consent
 * @param {boolean} additionalData.termsAccepted - Terms & Conditions accepted
 * @param {boolean} additionalData.privacyAccepted - Privacy Policy accepted
 * @param {boolean} additionalData.profileCompleted - Profile completion status
 * @returns {Promise<object>} The created user document
 */
export const initializeDefaultSettings = async (userId, email, additionalData = {}) => {
  try {
    const { db } = await getFirebaseInstances();
    const normalizedUserId = requireUserId(userId);

    const userDoc = {
      // Basic Identity
      uid: normalizedUserId,
      email: asTrimmedString(email),
      displayName: asTrimmedString(additionalData.displayName),
      fullName: additionalData.fullName || '',
      nickname: '',
      avatar: '',

      // Professional Information
      company: additionalData.company || '',
      jobTitle: additionalData.jobTitle || '',
      userType: additionalData.userType || '',

      // User Context
      useCase: additionalData.useCase || '',
      referralSource: additionalData.referralSource || '',
      country: additionalData.country || '',
      phoneNumber: additionalData.phoneNumber || '',

      // Privacy & Compliance
      marketingConsent: additionalData.marketingConsent || false,
      termsAccepted: additionalData.termsAccepted || false,
      privacyAccepted: additionalData.privacyAccepted || false,

      // Profile Status
      profileCompleted: additionalData.profileCompleted !== undefined ? additionalData.profileCompleted : true,

      // Default Settings
      ...DEFAULT_SETTINGS,

      // Timestamps
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const userDocRef = doc(db, 'users', normalizedUserId);
    await setDoc(userDocRef, userDoc, { merge: true });

    logger.info('Default user settings initialized for:', normalizedUserId);
    return userDoc;
  } catch (error) {
    logger.error('Error initializing default settings:', error);
    throw error;
  }
};

/**
 * Load all user settings from Firestore
 *
 * @param {string} userId - The user's UID
 * @returns {Promise<object>} User settings object
 */
export const loadUserSettings = async (userId) => {
  try {
    const normalizedUserId = requireUserId(userId);
    const { db } = await getFirebaseInstances();

    const userDocRef = doc(db, 'users', normalizedUserId);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      logger.warn('User document not found, initializing defaults');
      // If user document doesn't exist, create it with defaults
      const { auth } = await getFirebaseInstances();
      const currentUser = auth.currentUser;
      const email = currentUser?.email || '';
      return await initializeDefaultSettings(normalizedUserId, email);
    }

    const userData = userDocSnap.data();

    // Merge with defaults to ensure all fields exist
    const settings = {
      ...userData,
      appearance: {
        ...DEFAULT_SETTINGS.appearance,
        ...(userData.appearance || {}),
        theme: resolveActiveTheme(userData.appearance?.theme)
      },
      aiAssistant: {
        ...DEFAULT_SETTINGS.aiAssistant,
        ...(userData.aiAssistant || {})
      }
    };

    if (isIncompleteLocalInferenceSeed(settings.aiAssistant)) {
      settings.aiAssistant = {
        ...settings.aiAssistant,
        inferenceMode: 'cloud'
      };
      try {
        await setDoc(userDocRef, {
          aiAssistant: { inferenceMode: 'cloud' },
          updatedAt: new Date()
        }, { merge: true });
        logger.info('Migrated incomplete local inference seed to cloud for:', normalizedUserId);
      } catch (migrateError) {
        logger.warn('Failed to persist cloud inference default for:', normalizedUserId, migrateError);
      }
    }

    logger.info('User settings loaded for:', normalizedUserId);
    return settings;
  } catch (error) {
    logger.error('Error loading user settings:', error);
    throw error;
  }
};

/**
 * Save profile settings to Firestore
 *
 * @param {string} userId - The user's UID
 * @param {object} profileSettings - Profile settings object
 * @param {string} profileSettings.displayName - User's display name
 * @param {string} [profileSettings.nickname] - User's nickname
 * @param {string} [profileSettings.avatar] - User avatar initials or URL
 * @returns {Promise<{displayName: string, nickname: string, avatar: string}>}
 */
export const saveProfileSettings = async (userId, profileSettings) => {
  try {
    const normalizedUserId = requireUserId(userId);
    const normalizedProfile = normalizeProfileSettings(profileSettings);
    const { db, userDocRef } = await ensureUserDocument(normalizedUserId);

    const batch = writeBatch(db);
    const updatedAt = new Date();
    batch.set(userDocRef, {
      ...normalizedProfile,
      updatedAt
    }, { merge: true });
    batch.set(doc(db, 'user_public_profiles', normalizedUserId), {
      uid: normalizedUserId,
      ...normalizedProfile,
      updatedAt
    }, { merge: true });
    await batch.commit();

    logger.info('Profile settings saved for:', normalizedUserId);
    return normalizedProfile;
  } catch (error) {
    logger.error('Error saving profile settings:', error);
    throw error;
  }
};

/**
 * Save appearance settings to Firestore
 *
 * @param {string} userId - The user's UID
 * @param {object} appearanceSettings - Appearance settings object
 * @param {string} appearanceSettings.theme - Theme ('dark' or 'light'; dark is parked and saved as light)
 * @param {boolean} appearanceSettings.compactMode - Compact mode enabled
 * @param {string} appearanceSettings.fontSize - Font size ('small', 'medium', 'large')
 * @returns {Promise<{theme: string, compactMode: boolean, fontSize: string}>}
 */
export const saveAppearanceSettings = async (userId, appearanceSettings) => {
  try {
    const normalizedUserId = requireUserId(userId);
    const normalizedAppearance = normalizeAppearanceSettings(appearanceSettings);
    const { userDocRef } = await ensureUserDocument(normalizedUserId);

    await setDoc(userDocRef, {
      appearance: normalizedAppearance,
      updatedAt: new Date()
    }, { merge: true });

    logger.info('Appearance settings saved for:', normalizedUserId);
    return normalizedAppearance;
  } catch (error) {
    logger.error('Error saving appearance settings:', error);
    throw error;
  }
};

/**
 * Save AI assistant settings to Firestore
 *
 * @param {string} userId - The user's UID
 * @param {object} aiSettings - AI assistant settings object
 * @param {string} aiSettings.selectedModel - Selected AI model ID
 * @param {number} aiSettings.temperature - Temperature (0-1)
 * @param {number} aiSettings.maxTokens - Max tokens (512-4096)
 * @param {boolean} aiSettings.streamingEnabled - Streaming enabled
 * @returns {Promise<object>}
 */
export const saveAISettings = async (userId, aiSettings) => {
  try {
    const normalizedUserId = requireUserId(userId);
    const normalizedAISettings = normalizeAISettings(aiSettings);
    const { userDocRef } = await ensureUserDocument(normalizedUserId);

    await setDoc(userDocRef, {
      aiAssistant: normalizedAISettings,
      updatedAt: new Date()
    }, { merge: true });

    logger.info('AI assistant settings saved for:', normalizedUserId);
    return normalizedAISettings;
  } catch (error) {
    logger.error('Error saving AI assistant settings:', error);
    throw error;
  }
};

/**
 * Update a single user field in Firestore
 * Useful for updating individual preferences
 *
 * @param {string} userId - The user's UID
 * @param {string} field - Field name to update
 * @param {any} value - Value to set
 * @returns {Promise<void>}
 */
export const updateUserField = async (userId, field, value) => {
  try {
    const normalizedUserId = requireUserId(userId);
    const { userDocRef } = await ensureUserDocument(normalizedUserId);

    await setDoc(userDocRef, {
      [field]: value,
      updatedAt: new Date()
    }, { merge: true });

    logger.info(`User field '${field}' updated for:`, normalizedUserId);
  } catch (error) {
    logger.error(`Error updating user field '${field}':`, error);
    throw error;
  }
};
